import React, { useState } from 'react';
import {
  GetListParams,
  Record as RaRecord,
  useListContext,
  useNotify,
} from 'react-admin';
import Checkbox from '@material-ui/core/Checkbox';

import dataProvider from 'providers/dataProvider';
import { selectAllCapMessage, SELECT_ALL_MAX_ROWS } from './helpers';

export type SelectAllRowsOptions<RecordType extends RaRecord = RaRecord> = {
  /** Fields fetched beside `id` (e.g. `bumpResourceType`). */
  extraFields?: string[];
  /** Most rows one Select all may pick; above it the checkbox warns and selects nothing. */
  cap?: number;
  /** Receives the fetched rows so the caller can keep per-id data. */
  onRowsFetched?: (rows: RecordType[]) => void;
};

/**
 * Header checkbox selecting every row the current filter returns, across
 * pages: the grid's own header checkbox only knows the current page.
 */
export const SelectAllCheckbox = <RecordType extends RaRecord = RaRecord>({
  extraFields = [],
  cap = SELECT_ALL_MAX_ROWS,
  onRowsFetched,
}: SelectAllRowsOptions<RecordType>) => {
  const { resource, filterValues, currentSort, selectedIds, onSelect, total } =
    useListContext();
  const notify = useNotify();

  const [isFetching, setIsFetching] = useState(false);

  const selectedCount = selectedIds?.length ?? 0;
  const isAllSelected =
    selectedCount > 0 && total > 0 && selectedCount >= total;
  const isIndeterminate = selectedCount > 0 && !isAllSelected;

  const selectAll = async () => {
    if (total > cap) {
      notify(selectAllCapMessage(cap), 'warning');
      return;
    }

    setIsFetching(true);
    try {
      // The sort column must be selected: the sheets paginate through a
      // DISTINCT subquery, and Postgres rejects ordering by an unselected one.
      const fields = ['id', ...extraFields];
      if (currentSort?.field && !fields.includes(currentSort.field)) {
        fields.push(currentSort.field);
      }
      const params: GetListParams & { select: string[] } = {
        filter: filterValues,
        pagination: { page: 1, perPage: cap },
        sort: currentSort,
        select: fields,
      };
      // The raw provider, not the useDataProvider proxy: the proxy stores a
      // GET_LIST result as the resource's records, and these sparse rows would
      // replace the full page rows and blank the grid.
      const { data } = await dataProvider.getList<RecordType>(resource, params);
      onRowsFetched?.(data);
      onSelect(data.map(row => row.id));
    } catch (error) {
      notify(error.message || 'Failed to select all rows.', 'warning');
    } finally {
      setIsFetching(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      selectAll();
    } else {
      onSelect([]);
    }
  };

  return (
    <Checkbox
      color="primary"
      inputProps={{ 'aria-label': 'Select all' }}
      checked={isAllSelected}
      indeterminate={isIndeterminate}
      disabled={isFetching || total === 0}
      onChange={handleChange}
    />
  );
};

export default SelectAllCheckbox;
